import {
  DateRange,
  ListingType,
  SoldPropertiesSortOption,
} from '@app/models/enums'
import { ISoldProperty, SoldProperty } from '@app/models/SoldProperty'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface ISoldPropertiesListState {
  readonly dateRange: string
  readonly error?: string
  readonly isLoading: boolean
  readonly items: List<ISoldProperty>
  readonly listingType: ListingType
  readonly orderBy: string
  readonly sortOrder: SoldPropertiesSortOption
  readonly totalItems: number
}

const SoldPropertiesListStateRecord = Record({
  dateRange: DateRange.AllTime,
  error: undefined,
  isLoading: false,
  items: List<SoldProperty>(),
  listingType: '',
  orderBy: 'Ascending',
  sortOrder: SoldPropertiesSortOption.ClosingDate,
  totalItems: 0,
})

export class SoldPropertiesListState
  extends SoldPropertiesListStateRecord
  implements ISoldPropertiesListState
{
  public static fromJS(initialState?: Partial<ISoldPropertiesListState>) {
    if (!initialState) {
      return new SoldPropertiesListState(initialState)
    }

    const { items, ...rest } = initialState

    return new SoldPropertiesListState({
      items: items
        ? List(items.map((i) => SoldProperty.fromJS(i)))
        : List<SoldProperty>(),
      ...rest,
    })
  }

  public readonly dateRange: string
  public readonly error?: string
  public readonly isLoading: boolean
  public readonly items: List<ISoldProperty>
  public readonly listingType: ListingType
  public readonly orderBy: string
  public readonly sortOrder: SoldPropertiesSortOption
  public readonly totalItems: number

  constructor(props?: Partial<SoldPropertiesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadSoldProperties = 'SOLD_PROPERTIES_LIST::LOAD',
  LoadSoldPropertiesFailure = 'SOLD_PROPERTIES_LIST::LOAD_FAILURE',
  LoadSoldPropertiesRequest = 'SOLD_PROPERTIES_LIST::LOAD_REQUEST',
  LoadSoldPropertiesSuccess = 'SOLD_PROPERTIES_LIST::LOAD_SUCCESS',
  ResetState = 'SOLD_PROPERTIES_LIST::RESET_STATE',
  SetDateRange = 'SOLD_PROPERTIES_LIST::SET_DATE_RANGE',
  SetListingType = 'SOLD_PROPERTIES_LIST::SET_LISTING_TYPE',
  SetOrderBy = 'SOLD_PROPERTIES_LIST::SET_ORDER_BY',
  SetSortOrder = 'SOLD_PROPERTIES_LIST::SET_SORT_ORDER',
}

interface ILoadSoldPropertiesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadSoldProperties
}

interface ILoadSoldPropertiesFailureAction {
  error: string
  type: ActionTypes.LoadSoldPropertiesFailure
}

interface ILoadSoldPropertiesRequestAction {
  type: ActionTypes.LoadSoldPropertiesRequest
}

interface ILoadSoldPropertiesSuccessAction {
  append: boolean
  items: SoldProperty[]
  totalItems: number
  type: ActionTypes.LoadSoldPropertiesSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetDateRangeAction {
  dateRange: string
  type: ActionTypes.SetDateRange
}

interface ISetListingTypeAction {
  listingType: string
  type: ActionTypes.SetListingType
}

interface ISetOrderByAction {
  orderBy: string
  type: ActionTypes.SetOrderBy
}

interface ISetSortOrderAction {
  sortOrder: string
  type: ActionTypes.SetSortOrder
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadSoldPropertiesFailureAction
  | ILoadSoldPropertiesRequestAction
  | ILoadSoldPropertiesSuccessAction
  | IResetStateAction
  | ISetDateRangeAction
  | ISetListingTypeAction
  | ISetOrderByAction
  | ISetSortOrderAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadSoldProperties(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadSoldPropertiesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadSoldProperties,
  }
}

function loadSoldPropertiesFailure(
  error: string,
): ILoadSoldPropertiesFailureAction {
  return {
    error,
    type: ActionTypes.LoadSoldPropertiesFailure,
  }
}

function loadSoldPropertiesRequest(): ILoadSoldPropertiesRequestAction {
  return {
    type: ActionTypes.LoadSoldPropertiesRequest,
  }
}

function loadSoldPropertiesSuccess(
  items: SoldProperty[],
  append: boolean,
  totalItems: number,
): ILoadSoldPropertiesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadSoldPropertiesSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setDateRange(dateRange: string): ISetDateRangeAction {
  return {
    dateRange,
    type: ActionTypes.SetDateRange,
  }
}

function setListingType(listingType: string): ISetListingTypeAction {
  return {
    listingType,
    type: ActionTypes.SetListingType,
  }
}

function setOrderBy(orderBy: string): ISetOrderByAction {
  return {
    orderBy,
    type: ActionTypes.SetOrderBy,
  }
}

function setSortOrder(sortOrder: string): ISetSortOrderAction {
  return {
    sortOrder,
    type: ActionTypes.SetSortOrder,
  }
}

export const actionCreators = {
  loadSoldProperties,
  resetState,
  setDateRange,
  setListingType,
  setOrderBy,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SoldPropertiesListState> = (
  state: SoldPropertiesListState = new SoldPropertiesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadSoldPropertiesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as SoldPropertiesListState

    case ActionTypes.LoadSoldPropertiesRequest:
      return state.set('isLoading', true) as SoldPropertiesListState

    case ActionTypes.LoadSoldPropertiesSuccess: {
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<SoldProperty>)
        : List(action.items)

      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set(
          'totalItems',
          action.append ? state.totalItems : action.totalItems,
        ) as SoldPropertiesListState
    }

    case ActionTypes.ResetState:
      return new SoldPropertiesListState()

    case ActionTypes.SetDateRange:
      return state.set('dateRange', action.dateRange) as SoldPropertiesListState

    case ActionTypes.SetListingType:
      return state.set(
        'listingType',
        action.listingType,
      ) as SoldPropertiesListState

    case ActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as SoldPropertiesListState
    case ActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as SoldPropertiesListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadSoldPropertiesSaga(action: ILoadSoldPropertiesAction) {
  try {
    yield put(loadSoldPropertiesRequest())

    const currentState: SoldPropertiesListState = yield select(
      selectModuleState(),
    )

    const query: string[] = []

    if (currentState.dateRange !== DateRange.AllTime) {
      query.push(`date=${encodeURIComponent(currentState.dateRange)}`)
    }

    if (currentState.listingType) {
      query.push(`listingType=${encodeURIComponent(currentState.listingType)}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.sortOrder !== SoldPropertiesSortOption.ClosingDate) {
      query.push(`sort=${encodeURIComponent(currentState.sortOrder)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const url = `/api/BrokeragePipelines/SoldPropertiesReport${
      query.length > 0 ? `?${query.join('&')}` : ''
    }`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const soldProperties: SoldProperty[] = result.results.map((p: any) =>
        SoldProperty.fromJS(p),
      )
      yield put(
        loadSoldPropertiesSuccess(
          soldProperties,
          action.append,
          result.paging.total,
        ),
      )
    } else {
      yield put(loadSoldPropertiesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSoldPropertiesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadSoldProperties() {
  yield takeLatest(ActionTypes.LoadSoldProperties, loadSoldPropertiesSaga)
}

export function* soldPropertiesListRootSaga() {
  yield fork(watchLoadSoldProperties)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): SoldPropertiesListState =>
    state.soldPropertiesList

export const selectDateRange = () =>
  createSelector(selectModuleState(), (mState) => mState.dateRange)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectListingType = () =>
  createSelector(selectModuleState(), (mState) => mState.listingType)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
